import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Backdrop } from "@mui/material";
import logoMark from "../assets/images/company-logos/outbooks-logo.png";
import "./Loader.css";

// Rotating status lines shown under the logo while something loads
const LOADER_MESSAGES = [
  "Crunching the numbers…",
  "Balancing the books…",
  "Reconciling your figures…",
  "Preparing your pricing…",
];
const MESSAGE_INTERVAL_MS = 1800;

// Number of loaders currently mounted. Lets floating UI that sits above the
// backdrop (e.g. the AI chat widget) lock itself while anything is loading.
let activeLoaders = 0;
const listeners = new Set();
const notifyListeners = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getIsLoaderActive = () => activeLoaders > 0;

export const useIsLoaderActive = () =>
  useSyncExternalStore(subscribe, getIsLoaderActive);

// Full-screen loader shown during API calls / page loads.
// Same backdrop and stacking as before (blocks the page, sits above modals);
// only the look changed: the Outbooks mark in a spinning ring + rotating text.
const Loader = () => {
  const [open, setOpen] = useState(true);
  const [messageIndex, setMessageIndex] = useState(() =>
    Math.floor(Math.random() * LOADER_MESSAGES.length),
  );

  useEffect(() => {
    activeLoaders += 1;
    notifyListeners();
    return () => {
      activeLoaders -= 1;
      notifyListeners();
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(
      () => setMessageIndex((i) => (i + 1) % LOADER_MESSAGES.length),
      MESSAGE_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <Backdrop
      className="ob-loader"
      sx={{
        backgroundColor: "rgba(244, 247, 251, 0.78)",
        backdropFilter: "blur(3px)",
        zIndex: (theme) =>
          theme.zIndex.drawer < theme.zIndex.modal
            ? theme.zIndex.modal + 1
            : theme.zIndex.drawer + 1,
      }}
      open={open}
    >
      <div className="ob-loader__content" role="status" aria-live="polite">
        <div className="ob-loader__mark">
          <span className="ob-loader__ring" aria-hidden="true" />
          <img src={logoMark} alt="Outbooks" className="ob-loader__logo" />
        </div>
        <span key={messageIndex} className="ob-loader__text">
          {LOADER_MESSAGES[messageIndex]}
        </span>
      </div>
    </Backdrop>
  );
};

export default Loader;
